import {html, LitElement} from 'lit';
import {customElement, property, state} from 'lit/decorators.js';
import {classMap} from 'lit/directives/class-map.js';
import {unsafeHTML} from 'lit/directives/unsafe-html.js';
import {type Ref, ref, createRef} from 'lit/directives/ref.js';

import {AjaxP, Logger, PubSub} from '@mamdev/cad.static-next-lib';
import Viewport from '../../ts/services/viewport';
import {tablet} from '../../js/services/const';

import styles from './quiz.styles';
import elements from '../../ts/css/elements.styles';
import {iconsFont} from '../../ts/css/icons.styles';

import type {Question, QuizData, SavedAnswer} from './IQuizData';

@customElement('ui-quiz')
export class Quiz extends LitElement {
  static readonly styles = [iconsFont, elements, styles];

  @property() jsonUrl = '';

  @state() quizData: QuizData;
  @state() currentIndex = 0;
  @state() isInactive = false;

  private readonly anchorRef: Ref<HTMLElement> = createRef();

  private currentQuestion: Question;
  private maxQuestions: number;
  private readonly savedAnswers: SavedAnswer[] = [];
  private correctAnswerCount = 0;

  connectedCallback() {
    super.connectedCallback();

    AjaxP.getJSON<QuizData>(this.jsonUrl).then(result => {
      this.quizData = result;
      this.maxQuestions = this.quizData.question.length;
      this.currentQuestion = this.quizData.question[this.currentIndex];
    })
      .catch(e => Logger.logUnknownError(e, 'ui-quiz'));
  }

  handleAnswerClick(clickedAnswerIndex: number) {
    const savedAnswer = {} as SavedAnswer;
    const correctAnswerIndex = parseInt(this.currentQuestion.rightAnswer);
    savedAnswer.clickedAnswerIndex = clickedAnswerIndex;
    savedAnswer.clickedAnswerText = this.currentQuestion.answers[clickedAnswerIndex].answer;
    savedAnswer.correctAnswerText = this.currentQuestion.answers[correctAnswerIndex].answer;
    savedAnswer.questionIndex = this.currentIndex;
    this.savedAnswers.push(savedAnswer);

    if (clickedAnswerIndex === correctAnswerIndex) {
      this.correctAnswerCount++;
    }

    this.isInactive = true;
    this.currentIndex++;
    this.currentQuestion = this.quizData.question[this.currentIndex];

    let clickname = '&clickname=resultPage';
    if (this.currentIndex < this.maxQuestions) {
      clickname = '&clickname=question' + (this.currentIndex + 1);
    }

    if (window.utag_data) {
      window.utag_data.subContentIndex = (this.currentIndex + 1).toString();
    }

    PubSub.publish('quiz:setPixel', {
      referrer: '&referrer=undefined',
      click1: '&click1=undefined',
      click2: '&click2=undefined',
      click3: '&click3=undefined',
      click4: '&click4=undefined',
      clickname: clickname
    });

    window.setTimeout(() => {
      this.isInactive = false;
    }, 300);
  }

  handleReloadPageClick(event: Event) {
    event.preventDefault();
    window.location.reload();
  }

  render() {
    if (this.quizData) {
      if (this.currentIndex < this.maxQuestions) {
        return html`${this.getQuizPageTemplate()}`;
      } else {
        return html`${this.getResultPageTemplate()}`;
      }
    } else {
      return html`<div></div>`;
    }
  }

  updated() {
    if (this.currentIndex == this.maxQuestions || this.currentIndex === 0) {
      Viewport.scrollViewPortToTop();
    } else if (this.currentIndex > 0 && this.anchorRef.value) {
      const offsetTop = this.isMobileViewport() ? 100 : 125;
      Viewport.scrollViewPortTo(this.anchorRef.value, offsetTop);
    }
  }

  getQuizPageTemplate() {
    const classes = {
      active: true,
      inactive: this.isInactive
    };
    return html`
      <div class="quiz__wrapper">
        <slot name="quiz__content"></slot>
        <div class="quiz__label-wrapper">
          <div class="block-label" ${ref(this.anchorRef)}>
            <span class="question">Frage </span><span class="current-page">${this.currentIndex + 1}</span>/${this.maxQuestions}
          </div>
        </div>
        <div class="quiz__featured-image ${classMap(classes)}">
          <div class="picture-wrap">${unsafeHTML(this.currentQuestion.markup)}</div>
          ${this.currentQuestion.copyright ? html`<div class="copyright">${this.currentQuestion.copyright}</div>` : null}
        </div>
        <div class="currentQuestion">
          ${this.currentQuestion.text}
        </div>
        ${this.getPossibleAnswersTemplate()}
      </div>
    `;
  }

  getResultPageTemplate() {
    return html`
      <div class="quiz__wrapper">
        <div class="quiz__label-wrapper">
          <div class="block-label">
            <span class="question allAnswered">Quiz beendet</span>
          </div>
        </div>
        <div class="currentQuestion">Sie haben ${this.correctAnswerCount} von ${this.maxQuestions} Fragen richtig beantwortet</div>
        <ul class="quizSolutions">
          ${this.savedAnswers.map((savedAnswer, index) => this.getResultListTemplate(savedAnswer, index))}
        </ul>
        ${this.getReloadPageButtonTemplate()}
      </div>
    `;
  }

  getResultListTemplate(savedAnswer: SavedAnswer, index: number) {
    const pictureMarkup = this.quizData.question[savedAnswer.questionIndex].markup;
    const hasImage = !!pictureMarkup;
    return html`
      <li class="${hasImage ? 'hasImage' : ''}">
        <ul class="quizResponse">
          <li class="quizNumber">${String(index + 1).padStart(2, '0')}</li>
          ${this.getResultRightOrWrongTemplate(savedAnswer)}
          ${hasImage ? html`<li class="quizThumbnail">${unsafeHTML(pictureMarkup)}</li>` : null}
        </ul>
      </li>
    `;
  }

  getResultRightOrWrongTemplate(savedAnswer: SavedAnswer) {
    const question = this.quizData.question[savedAnswer.questionIndex].text;

    if (savedAnswer.clickedAnswerText === savedAnswer.correctAnswerText) {
      return html`
        <li class="rightOrWrong"><span class="icon icon-check"></span></li>
        <li class="quizQuestionAndAnswer">
        <div class="quizQuestion">${question}</div>
        <div class="quizRight">${savedAnswer.correctAnswerText}</div>
        </li>
      `;
    } else {
      return html`
        <li class="rightOrWrong"><span class="icon icon-close"></span></li>
        <li class="quizQuestionAndAnswer">
        <div class="quizQuestion">${question}</div>
        <div class="quizWrong">${savedAnswer.clickedAnswerText}</div>
        <div class="quizAnswer">Richtige Antwort: ${savedAnswer.correctAnswerText}</div>
        </li>
      `;
    }
  }

  getPossibleAnswersTemplate() {
    const alphabet = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z'];
    const classes = {
      active: true,
      inactive: this.isInactive
    };
    return html`
      <ul class="quizQuestions ${classMap(classes)}">
        ${this.currentQuestion.answers.map((answer, index) => html`
          <li><div @click=${() => this.handleAnswerClick(index)}>
            <span class="questionNumber">${alphabet[index]}</span>
            <span class="questionText">${answer.answer}</span>
          </div></li>
        `)}
      </ul>
      ${this.currentIndex > 0 ? this.getReloadPageButtonTemplate() : null}
    `;
  }

  getReloadPageButtonTemplate() {
    return html`
      <div class="quizButtonWrapper">
        <button class="quizRepeat" @click=${this.handleReloadPageClick}>Quiz erneut starten</button>
      </div>
    `;
  }

  isMobileViewport(): boolean {
    return window.innerWidth < tablet;
  }
}
